import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/app/auth';
import { ApiError, api } from '@/lib/api';
import { isTransient } from '@/lib/status';
import type { GreffonCategory, GreffonInstance, GreffonVersion } from './types';

function useAuthedQuery<T, S>(key: unknown[], path: string, select: (d: T) => S) {
  const isAuthenticated = useAuth((s) => s.isAuthenticated);
  return useQuery({ queryKey: key, queryFn: () => api.get<T>(path), select, enabled: isAuthenticated });
}

export function useCatalog() {
  return useAuthedQuery<{ greffons: GreffonVersion[] }, GreffonVersion[]>(
    ['catalog'],
    '/api/greffon/',
    (d) => d.greffons,
  );
}

export function useCategories() {
  return useAuthedQuery<{ categories: GreffonCategory[] }, GreffonCategory[]>(
    ['categories'],
    '/api/greffon/categories/',
    (d) => d.categories,
  );
}

export function useInstances() {
  const isAuthenticated = useAuth((s) => s.isAuthenticated);
  return useQuery({
    queryKey: ['instances'],
    // Unwrap in queryFn so query.state.data is the array (used by refetchInterval).
    queryFn: async () => (await api.get<{ greffons: GreffonInstance[] }>('/api/greffon/instances/')).greffons,
    enabled: isAuthenticated,
    // Poll faster while any instance is starting/stopping so the UI settles.
    // Otherwise keep a slow steady poll when the backend reports liveness: the
    // heartbeat-derived display_state (state-unknown / missing-on-greffer) flips
    // WHILE instances sit at rest, so a transition-only gate would never refresh
    // it. No liveness/display_state at all (legacy backend) → fetch-once.
    refetchInterval: (query) => {
      const list = query.state.data ?? [];
      // Fast-poll while any instance is transitioning OR mid-migration. A running
      // migration is invisible to display_state (its backstop bypasses the
      // op-window) and the instance can sit at STOPPED on the source, so the
      // transition gate misses it: active_migration is the only in-flight signal.
      // Only 'running' fast-polls; a recently-failed run is terminal and ages out.
      if (list.some((i) => isTransient(i.status) || i.active_migration?.status === 'running'))
        return 4000;
      return list.some((i) => i.greffer?.liveness != null || i.display_state != null) ? 15000 : false;
    },
  });
}

export function useStartStopInstance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action, l4Ack }: { id: string; action: 'start' | 'stop'; l4Ack?: boolean }) =>
      api.put<GreffonInstance>('/api/greffer/', {
        greffon_instance_id: id,
        action,
        // Only sent once the user acknowledges L4 exposure; the backend
        // requires it to start a greffon that exposes a raw (Tier-C) port.
        ...(l4Ack ? { l4_ack: true } : {}),
      }),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['instances'] });
      qc.invalidateQueries({ queryKey: ['subscription'] });
    },
  });
}

export function useDeleteInstance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      try {
        await api.del<void>(`/api/greffon/instances/${id}/`);
      } catch (e) {
        // 410 = already removed (concurrent delete) — treat as success.
        if (e instanceof ApiError && e.status === 410) return;
        throw e;
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['instances'] });
      qc.invalidateQueries({ queryKey: ['subscription'] });
    },
  });
}

export interface ConfigurationInput {
  // Create: send greffon_version_configuration_id. Update: send the existing
  // GreffonInstanceConfiguration row `id` (the backend branches on `id`).
  id?: number | string;
  greffon_version_configuration_id?: number;
  value: unknown;
}

export interface CreateInstancePayload {
  id?: string; // present → update (upsert update branch); absent → create
  name: string;
  greffon_version_id?: number; // required on create only
  greffer_id?: string;
  smtp_config_id?: string | null;
  configurations?: ConfigurationInput[];
}

export interface Backup {
  id: string;
  status: string; // running | success | failed
  kind: string; // backup | safety
  mode?: string; // hot (app-aware, no downtime) | cold (stop, snapshot, start)
  started_at: string;
  finished_at: string | null;
  bytes_added: number | null;
  error_code?: string;
  is_restorable?: boolean; // server: kind=backup AND status=success
}

/** Backup history for an instance. Polls while a backup is running so the list
 *  (and the row's running spinner) settles without a manual refresh. */
export function useBackups(instanceId: string, enabled = true) {
  const isAuthenticated = useAuth((s) => s.isAuthenticated);
  return useQuery({
    queryKey: ['backups', instanceId],
    queryFn: async () =>
      (await api.get<{ backups: Backup[] }>(`/api/greffer/instances/${instanceId}/backups/`)).backups,
    enabled: isAuthenticated && enabled,
    refetchInterval: (query) =>
      (query.state.data ?? []).some((b) => b.status === 'running') ? 4000 : false,
  });
}

/** Trigger a backup now (202 + background). The manager picks the mode server-side:
 *  hot (app-aware, zero-downtime) for a supported app, cold (brief stop) otherwise.
 *  Invalidates the history and the instance list (the latter drives the backing-up chip). */
export function useBackupNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ backup_id: string }>(`/api/greffer/instances/${id}/backup/`, {}),
    onSettled: (_d, _e, id) => {
      qc.invalidateQueries({ queryKey: ['backups', id] });
      qc.invalidateQueries({ queryKey: ['instances'] });
    },
  });
}

/** Restore-in-place from a snapshot. The backend requires confirm_name to equal
 *  the instance name (a blind/CSRF POST can't trigger a stop+overwrite).
 *  Invalidates the instance list (drives the restoring chip) + the history. */
export function useRestore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ instanceId, snapshotId, confirmName }: { instanceId: string; snapshotId: string; confirmName: string }) =>
      api.post<{ restore_id: string }>(`/api/greffer/instances/${instanceId}/restore/`, {
        snapshot_id: snapshotId,
        confirm_name: confirmName,
      }),
    onSettled: (_d, _e, vars) => {
      qc.invalidateQueries({ queryKey: ['instances'] });
      qc.invalidateQueries({ queryKey: ['backups', vars.instanceId] });
    },
  });
}

/** Trigger a cross-greffer MOVE (202 + async cutover). confirm_name-gated like
 *  restore (a blind/CSRF POST cannot move an app): the backend re-checks the
 *  typed name. The instance is stopped for a backstop snapshot, restored on the
 *  target, then the FK is repointed. Invalidates the instance list (drives the
 *  'Moving' chip + the active_migration the dialog reads) and the greffers list
 *  (running/usage shifts to the target once it completes). */
export function useMigrate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ instanceId, targetGrefferId, confirmName }: { instanceId: string; targetGrefferId: string; confirmName: string }) =>
      api.post<{ migration_id: string }>(`/api/greffer/instances/${instanceId}/migrate/`, {
        target_greffer_id: targetGrefferId,
        confirm_name: confirmName,
      }),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['instances'] });
      qc.invalidateQueries({ queryKey: ['greffers'] });
    },
  });
}

export interface BackupPolicy {
  enabled: boolean;
  daily_at: string | null; // "HH:MM"
  timezone: string;
  interval_seconds: number | null;
  keep_daily: number;
  keep_weekly: number;
  keep_monthly: number;
  next_due_at: string | null;
  last_backup_at: string | null;
  last_backup_status: string;
}

export function useBackupPolicy(instanceId: string, enabled = true) {
  const isAuthenticated = useAuth((s) => s.isAuthenticated);
  return useQuery({
    queryKey: ['backup-policy', instanceId],
    queryFn: () => api.get<BackupPolicy>(`/api/greffer/instances/${instanceId}/backup-policy/`),
    enabled: isAuthenticated && enabled,
  });
}

/** Set the per-instance backup schedule. The backend plan-gates the cadence
 *  (402 when faster than the plan floor). */
export function useSetBackupPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ instanceId, policy }: { instanceId: string; policy: Partial<BackupPolicy> }) =>
      api.put<BackupPolicy>(`/api/greffer/instances/${instanceId}/backup-policy/`, policy),
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['backup-policy', vars.instanceId] }),
  });
}

export function useCreateInstance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateInstancePayload) =>
      api.post<GreffonInstance>('/api/greffon/instances/', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['instances'] }),
  });
}
